'use client';

import React from 'react';
import {
	Popover as AriaPopover,
	composeRenderProps,
	OverlayArrow,
	PopoverContext,
	useSlottedContext,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';

import type { PopoverProps as AriaPopoverProps } from 'react-aria-components';

export interface PopoverProps extends Omit<AriaPopoverProps, 'children'> {
	showArrow?: boolean;
	children: React.ReactNode;
	theme?: 'light' | 'dark';
}

const styles = tv({
	base: 'rounded-xs border bg-clip-padding shadow-2xl',
	variants: {
		theme: {
			light: 'border-light-border-alt bg-neutral-white text-light-text',
			dark: 'border-dark-border-alt bg-dark-background text-dark-text',
		},
		isEntering: {
			true: 'animate-in fade-in placement-left:slide-in-from-right-1 placement-right:slide-in-from-left-1 placement-top:slide-in-from-bottom-1 placement-bottom:slide-in-from-top-1 duration-200 ease-out',
		},
		isExiting: {
			true: 'animate-out fade-out placement-left:slide-out-to-right-1 placement-right:slide-out-to-left-1 placement-top:slide-out-to-bottom-1 placement-bottom:slide-out-to-top-1 duration-150 ease-in',
		},
	},
	defaultVariants: {
		theme: 'light',
	},
});

export function Popover({ theme, children, showArrow, className, ...props }: PopoverProps) {
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const popoverContext = useSlottedContext(PopoverContext)!;
	const isSubmenu = popoverContext && popoverContext.trigger === 'SubmenuTrigger';
	let offset = showArrow ? 12 : 8;
	offset = isSubmenu ? offset - 6 : offset;

	return (
		<AriaPopover
			offset={offset}
			{...props}
			className={composeRenderProps(className, (className, renderProps) =>
				styles({ ...renderProps, className, theme }),
			)}
		>
			{showArrow && (
				<OverlayArrow className="group">
					<svg
						width={12}
						height={12}
						viewBox="0 0 12 12"
						className="group-placement-left:-rotate-90 group-placement-right:rotate-90 group-placement-bottom:rotate-180 fill-neutral-white block stroke-black/10 stroke-1"
					>
						<path d="M0 0 L6 6 L12 0" />
					</svg>
				</OverlayArrow>
			)}
			{children}
		</AriaPopover>
	);
}
